import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, X, Search, Pencil, Check } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { useGetOrgLabels } from '@/hooks/queries/use-get-org-labels';
import { useToggleWorkflowLabel } from '@/hooks/mutations/use-toggle-workflow-label';
import { CreateLabelModal } from '@/components/create-label-modal';
import { Label } from '@/types/workflow-types';
import { useWorkflowStore } from '@/stores/workflow-store';
import { AuthContext } from '@/context/auth-context';
import { cn } from '@/lib/utils';
import { orgPath } from '@/utils/org-path';
import { ROUTES } from '@/constants/routes';
import { getRecentLabelIds, pushRecentLabelId } from '@/utils/recent-labels';

const MAX_VISIBLE_LABELS = 5;

export const LabelBadges: FC<{
  labels?: Label[];
  maxVisible?: number;
  monochrome?: boolean;
  onRemove?: (labelId: string) => void;
  onContainerClick?: (e: React.MouseEvent) => void;
}> = ({
  labels,
  maxVisible = MAX_VISIBLE_LABELS,
  monochrome = false,
  onRemove,
  onContainerClick,
}) => {
  if (!labels || labels.length === 0) return null;
  const visible = labels.slice(0, maxVisible);
  const overflow = labels.slice(maxVisible);

  const renderChip = (label: Label) => {
    const color = label.color || '#9CA3AF';
    return (
      <span
        key={label.id}
        className={
          monochrome
            ? 'group/labelbadge relative inline-flex items-center rounded-md border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground'
            : 'group/labelbadge relative inline-flex items-center rounded-md px-1.5 py-1 text-xs font-medium text-foreground shadow-sm'
        }
        style={
          monochrome
            ? undefined
            : {
                border: `1px solid ${color}`,
                backgroundColor: `${color}20`,
              }
        }
      >
        {label.name}
        {onRemove && (
          <button
            type='button'
            aria-label={`Remove ${label.name} label`}
            className='absolute inset-y-0 right-0 flex items-center justify-center rounded-r-md pl-2 pr-1 text-foreground/60 opacity-0 transition-opacity hover:text-foreground group-hover/labelbadge:opacity-100 focus-visible:opacity-100'
            style={
              monochrome
                ? undefined
                : {
                    background: `linear-gradient(to right, transparent, color-mix(in srgb, ${color} 20%, var(--background)) 45%)`,
                  }
            }
            onClick={(e) => {
              e.stopPropagation();
              onRemove(label.id);
            }}
          >
            <X className='size-3' />
          </button>
        )}
      </span>
    );
  };

  return (
    <div
      className='flex items-center gap-1.5 flex-wrap'
      onClick={onContainerClick}
    >
      {visible.map(renderChip)}
      {overflow.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='text-xs text-muted-foreground'>
              +{overflow.length}
            </span>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <div className='flex flex-col gap-1'>
              {overflow.map((label) => (
                <span key={label.id}>{label.name}</span>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

const LabelOption: FC<{
  label: Label;
  applied: boolean;
  onToggle: () => void;
}> = ({ label, applied, onToggle }) => (
  <button
    type='button'
    onClick={onToggle}
    className='flex w-full items-center gap-2.5 h-8 px-2 rounded-md text-sm hover:bg-accent'
  >
    <span
      className='size-2.5 rounded-full shrink-0'
      style={{ backgroundColor: label.color || '#9CA3AF' }}
    />
    <span
      className={cn(
        'flex-1 truncate text-left text-foreground',
        applied && 'font-medium',
      )}
    >
      {label.name}
    </span>
    {applied && (
      <Check className='size-3.5 shrink-0 text-primary' strokeWidth={2.5} />
    )}
  </button>
);

interface UseLabelPickerOptions {
  workflowId: string;
  initialLabelIds: string[];
  orgId: string | undefined;
  onSuccess?: () => void;
  /** Stops click propagation on the popover content (useful in table rows). */
  stopPropagation?: boolean;
  /** Blocks outside pointer events so a dismiss click doesn't fall through to
   *  an underlying clickable row. */
  modal?: boolean;
}

export function useLabelPicker({
  workflowId,
  initialLabelIds,
  orgId,
  onSuccess,
  stopPropagation = false,
  modal = false,
}: UseLabelPickerOptions) {
  const [open, setOpen] = useState(false);
  // Live mirror of `open` so the async error handler can tell whether the
  // popover is still on screen without capturing a stale closure value. Synced
  // from state in an effect so it can't drift when the popover is closed via a
  // path other than handleOpen (e.g. opening the create modal or navigating).
  const openRef = useRef(false);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  const { data: allLabels = [] } = useGetOrgLabels({ enabled: !!orgId });
  const [hasEdited, setHasEdited] = useState(false);
  const [query, setQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pendingCreateName, setPendingCreateName] = useState('');
  // Frozen on open so the "Recently used" section doesn't reshuffle mid-toggle.
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const updateWorkflowLabels = useWorkflowStore((s) => s.updateWorkflowLabels);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Creating/managing labels is owner/admin-only (same gate as the settings page).
  const canCreate = !!orgId && !!(user?.isOwner || user?.isAdmin);

  const { assignedIds, toggle, initFromLabels } = useToggleWorkflowLabel({
    orgId,
    workflowId,
    onSuccess: () => {
      setHasEdited(true);
      onSuccess?.();
    },
    // A late failure that lands after the popover closed already wrote the
    // optimistic selection to the store; roll it back to the corrected set so
    // the row doesn't keep a chip the server rejected. While the popover is
    // still open the deferred effect handles the sync on close — writing now
    // would re-render the list (dropping the row under an active label filter)
    // and tear down the open dropdown mid-edit.
    onError: (revertedIds) => {
      if (openRef.current) return;
      updateWorkflowLabels(
        workflowId,
        allLabels.filter((l) => revertedIds.has(l.id)),
      );
    },
  });

  // Sync the store only while the popover is closed — syncing mid-interaction
  // re-renders the list and tears down the open popover mid-add.
  useEffect(() => {
    if (!hasEdited || open) return;
    const currentLabels = allLabels.filter((l) => assignedIds.has(l.id));
    updateWorkflowLabels(workflowId, currentLabels);
  }, [
    assignedIds,
    hasEdited,
    open,
    allLabels,
    workflowId,
    updateWorkflowLabels,
  ]);

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      initFromLabels(initialLabelIds);
      setHasEdited(false);
      setQuery('');
      setRecentIds(getRecentLabelIds(orgId));
    }
  };

  // Mark edited synchronously so closing before the request resolves doesn't
  // let the sync effect reset assignedIds. Applying (not removing) records a
  // recent.
  const toggleLabel = (labelId: string) => {
    setHasEdited(true);
    if (!assignedIds.has(labelId)) pushRecentLabelId(orgId, labelId);
    toggle(labelId);
  };

  const normalizedQuery = query.trim().toLowerCase();

  const searchResults = useMemo(
    () =>
      allLabels.filter((l) => l.name.toLowerCase().includes(normalizedQuery)),
    [allLabels, normalizedQuery],
  );

  const recentLabels = recentIds
    .map((id) => allLabels.find((l) => l.id === id))
    .filter((l): l is Label => !!l);
  const recentIdSet = new Set(recentLabels.map((l) => l.id));
  const otherLabels = allLabels.filter((l) => !recentIdSet.has(l.id));

  const exactMatch = allLabels.some(
    (l) => l.name.toLowerCase() === normalizedQuery,
  );
  const showCreateRow = canCreate && normalizedQuery.length > 0 && !exactMatch;

  const openCreateModal = () => {
    setPendingCreateName(query.trim());
    setOpen(false);
    setCreateModalOpen(true);
  };

  const handleLabelCreated = (label: Label) => {
    setHasEdited(true);
    pushRecentLabelId(orgId, label.id);
    toggle(label.id);
    // The new label isn't in `allLabels` yet — reflect it in the store now.
    updateWorkflowLabels(workflowId, [
      ...allLabels.filter((l) => assignedIds.has(l.id)),
      label,
    ]);
  };

  const goToManageLabels = () => {
    setOpen(false);
    navigate(orgPath(ROUTES.SETTINGS.LABELS, orgId));
  };

  // Track incoming labels while closed so the badge (x) can remove without
  // opening the popover first.
  const initialKey = initialLabelIds.join(',');
  useEffect(() => {
    if (open || hasEdited) return;
    initFromLabels(initialLabelIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey, open, hasEdited]);

  const displayLabels =
    open || hasEdited
      ? allLabels.filter((l) => assignedIds.has(l.id))
      : allLabels.filter((l) => initialLabelIds.includes(l.id));

  const listContent = (
    <>
      <div className='flex items-center gap-2 h-9 px-3 border-b border-border'>
        <Search className='size-3.5 shrink-0 text-muted-foreground' />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && showCreateRow) {
              e.preventDefault();
              openCreateModal();
            }
          }}
          placeholder={canCreate ? 'Search or create a label' : 'Search labels'}
          autoFocus
          className='flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none'
        />
      </div>

      <div className='max-h-64 overflow-y-auto p-1.5 subtle-scrollbar'>
        {normalizedQuery ? (
          <>
            {searchResults.map((label) => (
              <LabelOption
                key={label.id}
                label={label}
                applied={assignedIds.has(label.id)}
                onToggle={() => toggleLabel(label.id)}
              />
            ))}
            {searchResults.length === 0 && (
              <div className='px-2 py-2.5 text-sm text-muted-foreground'>
                No labels match.
              </div>
            )}
          </>
        ) : (
          <>
            {recentLabels.length > 0 && (
              <>
                <div className='px-2 pt-1.5 pb-1 text-xs font-medium text-muted-foreground'>
                  Recently used
                </div>
                {recentLabels.map((label) => (
                  <LabelOption
                    key={label.id}
                    label={label}
                    applied={assignedIds.has(label.id)}
                    onToggle={() => toggleLabel(label.id)}
                  />
                ))}
                <div className='my-1.5 mx-2 h-px bg-border' />
              </>
            )}
            {recentLabels.length > 0 && otherLabels.length > 0 && (
              <div className='px-2 pt-0.5 pb-1 text-xs font-medium text-muted-foreground'>
                All labels
              </div>
            )}
            {otherLabels.map((label) => (
              <LabelOption
                key={label.id}
                label={label}
                applied={assignedIds.has(label.id)}
                onToggle={() => toggleLabel(label.id)}
              />
            ))}
            {allLabels.length === 0 && (
              <div className='px-2 py-2.5 text-sm text-muted-foreground'>
                No labels yet.
              </div>
            )}
          </>
        )}
        {showCreateRow && (
          <button
            type='button'
            onClick={openCreateModal}
            className='mt-0.5 flex w-full items-center gap-2.5 min-h-8 px-2 py-1 rounded-md text-sm text-foreground bg-accent/50 hover:bg-accent'
          >
            <Plus className='size-3.5 shrink-0 text-primary' />
            <span className='truncate text-left'>
              Create new label &ldquo;{query.trim()}&rdquo;
            </span>
          </button>
        )}
      </div>

      {canCreate && (
        <button
          type='button'
          onClick={goToManageLabels}
          className='flex w-full items-center gap-2 h-9 px-3.5 border-t border-border text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground'
        >
          <Pencil className='size-3.5' />
          <span>Manage labels</span>
        </button>
      )}
    </>
  );

  const renderPopover = (trigger: React.ReactNode) => (
    <Popover open={open} onOpenChange={handleOpen} modal={modal}>
      {trigger}
      <PopoverContent
        className='w-72 p-0 overflow-hidden'
        align='start'
        onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      >
        {listContent}
      </PopoverContent>
    </Popover>
  );

  const createModalEl = canCreate ? (
    <CreateLabelModal
      isOpen={createModalOpen}
      onOpenChange={setCreateModalOpen}
      initialName={pendingCreateName}
      onCreated={handleLabelCreated}
    />
  ) : null;

  return {
    open,
    displayLabels,
    toggleLabel,
    hasLabels: displayLabels.length > 0,
    canCreate,
    renderPopover,
    createModalEl,
  };
}

interface LabelPickerProps {
  workflowId: string;
  initialLabelIds: string[];
  orgId: string | undefined;
  onSuccess?: () => void;
  /** When true, shows static badges with no popover. */
  readOnly?: boolean;
  /** Stops click propagation on the trigger (useful in table rows). */
  stopPropagation?: boolean;
  /** Custom trigger element. When provided, badges are NOT rendered inside the
   *  trigger — use `renderBadges` callback to place them elsewhere. */
  customTrigger?: React.ReactNode;
  /** Replaces the wrapper's layout classes, e.g. `contents` to let the trigger
   *  and badges lay out against the caller's own row. */
  className?: string;
  /** Render prop that receives the current display labels and a remover. Only
   *  useful together with `customTrigger` to render badges outside the popover
   *  trigger. Pass `onRemove` to `LabelBadges` to show an (x) on each badge. */
  renderBadges?: (
    labels: Label[],
    onRemove: (labelId: string) => void,
  ) => React.ReactNode;
}

export const LabelPicker: FC<LabelPickerProps> = ({
  workflowId,
  initialLabelIds,
  orgId,
  onSuccess,
  readOnly = false,
  stopPropagation = false,
  customTrigger,
  renderBadges,
  className,
}) => {
  const picker = useLabelPicker({
    workflowId,
    initialLabelIds,
    orgId,
    onSuccess,
    stopPropagation,
  });

  if (readOnly) {
    return <LabelBadges labels={picker.displayLabels} maxVisible={Infinity} />;
  }

  const defaultButton = (
    <button
      type='button'
      aria-label={picker.hasLabels ? 'Add label' : undefined}
      className={
        picker.hasLabels
          ? 'inline-flex size-[26px] items-center justify-center rounded-md border border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer'
          : 'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground bg-muted border border-border hover:bg-accent hover:text-foreground transition-colors cursor-pointer'
      }
    >
      <Plus className='size-3.5' />
      {!picker.hasLabels && 'Add Label'}
    </button>
  );

  return (
    <div
      className={cn(
        'flex flex-wrap gap-1.5 items-center min-h-[28px]',
        className,
      )}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
    >
      {picker.renderPopover(
        <PopoverTrigger asChild>
          {customTrigger ?? defaultButton}
        </PopoverTrigger>,
      )}
      {renderBadges?.(picker.displayLabels, picker.toggleLabel)}
      {picker.createModalEl}
    </div>
  );
};
